import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { catchError, from, switchMap, throwError } from 'rxjs';

import { AuthSessionService } from '../../features/auth/services/auth-session.service';

const AUTH_RETRY = new HttpContextToken<boolean>(() => false);
let refreshPromise: Promise<string | null> | null = null;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authSession = inject(AuthSessionService);
  const router = inject(Router);
  const accessToken = authSession.accessToken();
  const authorizedRequest =
    accessToken && !req.headers.has('Authorization') && !isTokenExchangeEndpoint(req.url)
      ? withAccessToken(req, accessToken)
      : req;

  return next(authorizedRequest).pipe(
    catchError((error: unknown) => {
      if (!shouldRefreshToken(req, error)) {
        return throwError(() => error);
      }

      return from(refreshAccessToken(authSession)).pipe(
        switchMap((newAccessToken) => {
          if (!newAccessToken) {
            void router.navigate(['/inicio-sesion']);
            return throwError(() => error);
          }

          return next(
            withAccessToken(
              req.clone({ context: req.context.set(AUTH_RETRY, true) }),
              newAccessToken,
            ),
          );
        }),
        catchError((refreshError: unknown) => {
          authSession.clearSession();
          void router.navigate(['/inicio-sesion']);

          return throwError(() => refreshError);
        }),
      );
    }),
  );
};

function shouldRefreshToken(req: HttpRequest<unknown>, error: unknown): boolean {
  return (
    error instanceof HttpErrorResponse &&
    error.status === 401 &&
    !req.context.get(AUTH_RETRY) &&
    !isTokenExchangeEndpoint(req.url)
  );
}

function isTokenExchangeEndpoint(url: string): boolean {
  return /\/auth\/(login|refresh)$/.test(url);
}

function withAccessToken(req: HttpRequest<unknown>, accessToken: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

function refreshAccessToken(authSession: AuthSessionService): Promise<string | null> {
  refreshPromise ??= authSession
    .refresh()
    .then(() => authSession.accessToken())
    .catch(() => {
      authSession.clearSession();
      return null;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}
